import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomAvatar from '../../components/shared/CustomAvatar';
import BtButton from '../bt-button/bt-button';
import { SupplierSummary } from '../../services/service-evaluations';
import { formatStockholmDay } from './reviewFormat';
import ReadOnlyStars from './ReadOnlyStars';
import styles from './page-supplier-evaluations.module.scss';

const VISIBLE_COMMENTS = 6;

type Props = {
  comments: SupplierSummary['comments'];
  onShowAnswer: (responseId: string) => void;
};

const CommentList: FC<Props> = ({ comments, onShowAnswer }) => {
  const [t] = useTranslation('evaluations');
  const [expanded, setExpanded] = useState(false);

  if (!comments.length) {
    return (
      <span className={styles.muted}>
        {t('review.comments.none', 'Inga kommentarer som du har behörighet att se.')}
      </span>
    );
  }

  const hiddenCount = comments.length - VISIBLE_COMMENTS;
  const shown = expanded ? comments : comments.slice(0, VISIBLE_COMMENTS);

  return (
    <ul className={styles.comments}>
      {shown.map((comment, index) => (
        <li key={`${comment.responseId}-${index}`} className={styles.comment}>
          <CustomAvatar alt={comment.respondentName} size="32px" />
          <div className={styles.commentBody}>
            <div className={styles.commentMeta}>
              <span className={styles.author}>{comment.respondentName}</span>
              <span className={`${styles.muted} ${styles.small}`}>
                {[comment.projectName, formatStockholmDay(comment.submittedAt)]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
            <div className={styles.question}>
              {comment.skipped && (
                <span className={styles.skipped}>{t('answer.skip', 'Vet ej')}</span>
              )}
              {!comment.skipped && comment.score !== null && (
                <ReadOnlyStars rating={comment.score} />
              )}
              <span className={styles.commentQuestion}>{comment.questionText}</span>
            </div>
            <p className={`${styles.commentText} ${styles.quote}`}>{comment.comment}</p>
          </div>
          <div className={styles.commentAction}>
            <BtButton size="xxs" color="white" onClick={() => onShowAnswer(comment.responseId)}>
              {t('review.comments.showAnswer', 'Visa svar')}
            </BtButton>
          </div>
        </li>
      ))}
      {hiddenCount > 0 && (
        <li className={styles.commentsToggle}>
          <BtButton size="small" color="white" onClick={() => setExpanded(open => !open)}>
            {expanded
              ? t('review.comments.showLess', 'Visa färre')
              : t('review.comments.showMore', {
                  count: hiddenCount,
                  defaultValue_one: 'Visa fler kommentarer ({{count}})',
                  defaultValue_other: 'Visa fler kommentarer ({{count}})',
                })}
          </BtButton>
        </li>
      )}
    </ul>
  );
};

export default CommentList;
